import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUser } from "@supabase/auth-helpers-react";
import { useIsAdmin } from "@/hooks/useUserRole";

export interface StudyModule {
  id: string;
  category: string;
  title: string;
  description: string | null;
  lesson_content: string | null;
  difficulty: string;
  order_index: number;
  is_active: boolean;
  is_free?: boolean;
  // Joined data
  progress_status?: string;
  quiz_accuracy?: number;
  completed_at?: string | null;
  quizCount?: number;
}

export function useStudyTrail(category: string, isPremium: boolean = false, isTrialActive: boolean = false) {
  const user = useUser();
  const queryClient = useQueryClient();
  const { isAdmin } = useIsAdmin();

  const { data: modules = [], isLoading } = useQuery({
    queryKey: ["study-trail", category, user?.id],
    queryFn: async () => {
      // Fetch modules
      // Fetch modules — admins see all (including inactive), candidates only active
      let query = supabase
        .from("study_trail_modules")
        .select("*")
        .eq("category", category)
        .order("order_index", { ascending: true });

      if (!isAdmin) {
        query = query.eq("is_active", true);
      }

      const { data: modulesData, error: modulesError } = await query;

      if (modulesError) throw modulesError;
      if (!modulesData || modulesData.length === 0) return [];

      // Fetch user progress if logged in
      let progressMap: Record<string, { status: string; quiz_accuracy: number; completed_at: string | null }> = {};
      if (user?.id) {
        const { data: progressData } = await supabase
          .from("user_module_progress")
          .select("*")
          .eq("user_id", user.id);

        if (progressData) {
          progressData.forEach((p: any) => {
            progressMap[p.module_id] = {
              status: p.status,
              quiz_accuracy: Number(p.quiz_accuracy) || 0,
              completed_at: p.completed_at,
            };
          });
        }
      }

      // Fetch quiz counts per module
      const moduleIds = modulesData.map(m => m.id);
      const { data: quizCounts } = await supabase
        .from("quizzes")
        .select("module_id")
        .in("module_id", moduleIds)
        .eq("is_active", true);

      const quizCountMap: Record<string, number> = {};
      quizCounts?.forEach((q: any) => {
        quizCountMap[q.module_id] = (quizCountMap[q.module_id] || 0) + 1;
      });


      // Map modules with progress
      return modulesData.map((m, index) => {
        const progress = progressMap[m.id];
        const moduleIsFree = (m as any).is_free === true;
        // Trial libera APENAS o 1º módulo (order_index=0) de cada trilha
        const trialCanAccess = isTrialActive && index === 0;
        const hasAccess = isAdmin || isPremium || moduleIsFree || trialCanAccess;

        // Determine status
        let status = "locked";
        if (isAdmin) {
          status = progress?.status || "in_progress";
        } else if (!hasAccess) {
          status = "locked";
        } else if (progress) {
          status = progress.status;
        } else if (index === 0) {
          status = "in_progress";
        } else {
          // Check sequential unlock only among accessible modules
          const prevModule = modulesData[index - 1];
          const prevProgress = progressMap[prevModule.id];
          if (prevProgress?.status === "completed") {
            status = "in_progress";
          }
        }

        return {
          ...m,
          is_free: moduleIsFree,
          progress_status: status,
          quiz_accuracy: progress?.quiz_accuracy || 0,
          completed_at: progress?.completed_at || null,
          quizCount: quizCountMap[m.id] || 0,
        } as StudyModule;
      });
    },
    enabled: !!category,
  });

  const updateProgress = useMutation({
    mutationFn: async ({ moduleId, status, quizAccuracy }: { moduleId: string; status: string; quizAccuracy?: number }) => {
      if (!user?.id) throw new Error("User not logged in");

      const updateData: any = {
        user_id: user.id,
        module_id: moduleId,
        status,
      };

      if (status === "completed") {
        updateData.completed_at = new Date().toISOString();
      }
      if (quizAccuracy !== undefined) {
        updateData.quiz_accuracy = quizAccuracy;
      }

      const { error } = await supabase
        .from("user_module_progress")
        .upsert(updateData, { onConflict: "user_id,module_id" });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["study-trail", category] });
    },
  });

  const completedCount = modules.filter(m => m.progress_status === "completed").length;
  const totalCount = modules.length;
  const trailProgress = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return {
    modules,
    isLoading,
    updateProgress,
    completedCount,
    totalCount,
    trailProgress,
  };
}
